// useViewModalState.js

import { useCallback, useEffect, useState } from 'react';

import {
  hydrateViewEditorDraft,
  normalizeBoardEnumOptions,
  normalizeChartConfig,
} from '../viewEditorDraft';
import { normalizeBoardLayout } from 'utilities/BoardLayout';

// Manages ViewModal state and keeps hydration logic in one place.
const useViewModalState = ({
  initialViewType = 'LIST',
  isOpen = true,
  isEditMode = false,
  view = null,
  draft: controlledDraft,
  onDraftChange,
} = {}) => {
  const isControlled = controlledDraft !== undefined && typeof onDraftChange === 'function';

  const [type, setType] = useState('LIST');
  const [boardField, setBoardField] = useState('');
  const [boardLayout, setBoardLayout] = useState(() => normalizeBoardLayout(null));
  const [boardEnumOptions, setBoardEnumOptions] = useState(() => normalizeBoardEnumOptions(null));
  const [viewGroupId, setViewGroupId] = useState('');
  const [properties, setProperties] = useState([]);
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [filters, setFilters] = useState([]);
  const [sorts, setSorts] = useState([]);
  const [includeDescendants, setIncludeDescendants] = useState(false);
  const [timelineField, setTimelineField] = useState('');
  const [timelineRowMode, setTimelineRowMode] = useState('flat');
  const [timelineGroupField, setTimelineGroupField] = useState('');
  const [showUnscheduled, setShowUnscheduled] = useState(true);
  const [preset, setPreset] = useState('timeline');
  const [showHierarchy, setShowHierarchy] = useState(false);
  const [showDependencies, setShowDependencies] = useState(false);
  const [allowScheduleEdit, setAllowScheduleEdit] = useState(false);
  const [calendarField, setCalendarField] = useState('');
  const [chartConfig, setChartConfig] = useState(() => normalizeChartConfig(null));
  const [scheduledType, setScheduledType] = useState('');
  const [scheduledField, setScheduledField] = useState('');
  const [slotIncrementMinutes, setSlotIncrementMinutes] = useState('15');
  const [scheduledFilters, setScheduledFilters] = useState([]);
  const [scheduledProperties, setScheduledProperties] = useState([]);

  useEffect(() => {
    if (isControlled || !isOpen) return;

    const nextDraft = hydrateViewEditorDraft(view, {
      initialViewType,
      isEditMode: isEditMode && Boolean(view),
    });

    setDescription(nextDraft.description);
    setTitle(nextDraft.title);
    setTitleTouched(nextDraft.titleTouched);
    setType(nextDraft.type);
    setBoardField(nextDraft.boardField);
    setBoardLayout(normalizeBoardLayout(nextDraft.boardLayout));
    setBoardEnumOptions(nextDraft.boardEnumOptions);
    setViewGroupId(nextDraft.viewGroupId || '');
    setProperties(nextDraft.properties);
    setFilters(nextDraft.filters);
    setSorts(nextDraft.sorts);
    setIncludeDescendants(nextDraft.includeDescendants);
    setTimelineField(nextDraft.timelineField);
    setTimelineRowMode(nextDraft.timelineRowMode);
    setTimelineGroupField(nextDraft.timelineGroupField);
    setShowUnscheduled(nextDraft.showUnscheduled);
    setPreset(nextDraft.preset || 'timeline');
    setShowHierarchy(Boolean(nextDraft.showHierarchy));
    setShowDependencies(Boolean(nextDraft.showDependencies));
    setAllowScheduleEdit(Boolean(nextDraft.allowScheduleEdit));
    setCalendarField(nextDraft.calendarField || '');
    setChartConfig(nextDraft.chartConfig);
    setScheduledType(nextDraft.scheduledType);
    setScheduledField(nextDraft.scheduledField);
    setSlotIncrementMinutes(nextDraft.slotIncrementMinutes);
    setScheduledFilters(nextDraft.scheduledFilters);
    setScheduledProperties(nextDraft.scheduledProperties);
  }, [initialViewType, isControlled, isEditMode, isOpen, view]);

  const resolvedDraft = isControlled
    ? controlledDraft
    : {
      allowScheduleEdit,
      boardEnumOptions,
      boardField,
      boardLayout,
      viewGroupId,
      calendarField,
      chartConfig,
      description,
      filters,
      includeDescendants,
      preset,
      properties,
      scheduledField,
      scheduledFilters,
      scheduledProperties,
      scheduledType,
      showDependencies,
      showHierarchy,
      showUnscheduled,
      slotIncrementMinutes,
      sorts,
      timelineField,
      timelineGroupField,
      timelineRowMode,
      title,
      titleTouched,
      type,
    };

  const patchControlledDraft = useCallback((patch) => {
    if (!isControlled || !patch || typeof patch !== 'object') return;
    onDraftChange({
      ...controlledDraft,
      ...patch,
    });
  }, [controlledDraft, isControlled, onDraftChange]);

  const resolvedSetType = isControlled
    ? (value) => patchControlledDraft({ type: typeof value === 'function' ? value(resolvedDraft.type) : value })
    : setType;
  const resolvedSetBoardField = isControlled
    ? (value) => patchControlledDraft({ boardField: typeof value === 'function' ? value(resolvedDraft.boardField) : value })
    : setBoardField;
  const resolvedSetBoardLayout = isControlled
    ? (value) => patchControlledDraft({ boardLayout: typeof value === 'function' ? value(resolvedDraft.boardLayout) : value })
    : setBoardLayout;
  const resolvedSetBoardEnumOptions = isControlled
    ? (value) => patchControlledDraft({
      boardEnumOptions: typeof value === 'function' ? value(resolvedDraft.boardEnumOptions) : value,
    })
    : setBoardEnumOptions;
  const resolvedSetViewGroupId = isControlled
    ? (value) => patchControlledDraft({
      viewGroupId: typeof value === 'function' ? value(resolvedDraft.viewGroupId) : value,
    })
    : setViewGroupId;
  const resolvedSetProperties = isControlled
    ? (value) => patchControlledDraft({ properties: typeof value === 'function' ? value(resolvedDraft.properties) : value })
    : setProperties;
  const resolvedSetDescription = isControlled
    ? (value) => patchControlledDraft({ description: typeof value === 'function' ? value(resolvedDraft.description) : value })
    : setDescription;
  const resolvedSetTitle = isControlled
    ? (value) => patchControlledDraft({ title: typeof value === 'function' ? value(resolvedDraft.title) : value })
    : setTitle;
  const resolvedSetTitleTouched = isControlled
    ? (value) => patchControlledDraft({
      titleTouched: typeof value === 'function' ? value(resolvedDraft.titleTouched) : value,
    })
    : setTitleTouched;
  const resolvedSetFilters = isControlled
    ? (value) => patchControlledDraft({ filters: typeof value === 'function' ? value(resolvedDraft.filters) : value })
    : setFilters;
  const resolvedSetSorts = isControlled
    ? (value) => patchControlledDraft({ sorts: typeof value === 'function' ? value(resolvedDraft.sorts) : value })
    : setSorts;
  const resolvedSetIncludeDescendants = isControlled
    ? (value) => patchControlledDraft({
      includeDescendants: typeof value === 'function' ? value(resolvedDraft.includeDescendants) : value,
    })
    : setIncludeDescendants;
  const resolvedSetTimelineField = isControlled
    ? (value) => patchControlledDraft({
      timelineField: typeof value === 'function' ? value(resolvedDraft.timelineField) : value,
    })
    : setTimelineField;
  const resolvedSetTimelineRowMode = isControlled
    ? (value) => patchControlledDraft({
      timelineRowMode: typeof value === 'function' ? value(resolvedDraft.timelineRowMode) : value,
    })
    : setTimelineRowMode;
  const resolvedSetTimelineGroupField = isControlled
    ? (value) => patchControlledDraft({
      timelineGroupField: typeof value === 'function' ? value(resolvedDraft.timelineGroupField) : value,
    })
    : setTimelineGroupField;
  const resolvedSetShowUnscheduled = isControlled
    ? (value) => patchControlledDraft({
      showUnscheduled: typeof value === 'function' ? value(resolvedDraft.showUnscheduled) : value,
    })
    : setShowUnscheduled;
  const resolvedSetPreset = isControlled
    ? (value) => patchControlledDraft({
      preset: typeof value === 'function' ? value(resolvedDraft.preset) : value,
    })
    : setPreset;
  const resolvedSetShowHierarchy = isControlled
    ? (value) => patchControlledDraft({
      showHierarchy: typeof value === 'function' ? value(resolvedDraft.showHierarchy) : value,
    })
    : setShowHierarchy;
  const resolvedSetShowDependencies = isControlled
    ? (value) => patchControlledDraft({
      showDependencies: typeof value === 'function' ? value(resolvedDraft.showDependencies) : value,
    })
    : setShowDependencies;
  const resolvedSetAllowScheduleEdit = isControlled
    ? (value) => patchControlledDraft({
      allowScheduleEdit: typeof value === 'function' ? value(resolvedDraft.allowScheduleEdit) : value,
    })
    : setAllowScheduleEdit;

  const applyTypeSelection = useCallback((selection) => {
    if (!selection || typeof selection !== 'object') return;
    const next = {
      type: selection.type,
      preset: selection.preset,
      showHierarchy: Boolean(selection.showHierarchy),
      showDependencies: Boolean(selection.showDependencies),
      allowScheduleEdit: Boolean(selection.allowScheduleEdit),
    };
    if (isControlled) {
      patchControlledDraft(next);
      return;
    }
    setType(next.type);
    setPreset(next.preset);
    setShowHierarchy(next.showHierarchy);
    setShowDependencies(next.showDependencies);
    setAllowScheduleEdit(next.allowScheduleEdit);
  }, [isControlled, patchControlledDraft]);

  const resolvedSetChartConfig = isControlled
    ? (value) => patchControlledDraft({
      chartConfig: typeof value === 'function' ? value(resolvedDraft.chartConfig) : value,
    })
    : setChartConfig;
  const resolvedSetScheduledType = isControlled
    ? (value) => patchControlledDraft({
      scheduledType: typeof value === 'function' ? value(resolvedDraft.scheduledType) : value,
    })
    : setScheduledType;
  const resolvedSetScheduledField = isControlled
    ? (value) => patchControlledDraft({
      scheduledField: typeof value === 'function' ? value(resolvedDraft.scheduledField) : value,
    })
    : setScheduledField;
  const resolvedSetSlotIncrementMinutes = isControlled
    ? (value) => patchControlledDraft({
      slotIncrementMinutes: typeof value === 'function' ? value(resolvedDraft.slotIncrementMinutes) : value,
    })
    : setSlotIncrementMinutes;
  const resolvedSetScheduledFilters = isControlled
    ? (value) => patchControlledDraft({
      scheduledFilters: typeof value === 'function' ? value(resolvedDraft.scheduledFilters) : value,
    })
    : setScheduledFilters;
  const resolvedSetScheduledProperties = isControlled
    ? (value) => patchControlledDraft({
      scheduledProperties: typeof value === 'function' ? value(resolvedDraft.scheduledProperties) : value,
    })
    : setScheduledProperties;

  return {
    type: resolvedDraft.type,
    setType: resolvedSetType,
    boardField: resolvedDraft.boardField,
    setBoardField: resolvedSetBoardField,
    boardLayout: normalizeBoardLayout(resolvedDraft.boardLayout),
    setBoardLayout: resolvedSetBoardLayout,
    boardEnumOptions: resolvedDraft.boardEnumOptions,
    setBoardEnumOptions: resolvedSetBoardEnumOptions,
    viewGroupId: resolvedDraft.viewGroupId || '',
    setViewGroupId: resolvedSetViewGroupId,
    properties: resolvedDraft.properties,
    setProperties: resolvedSetProperties,
    description: resolvedDraft.description,
    setDescription: resolvedSetDescription,
    title: resolvedDraft.title,
    setTitle: resolvedSetTitle,
    titleTouched: resolvedDraft.titleTouched,
    setTitleTouched: resolvedSetTitleTouched,
    filters: resolvedDraft.filters,
    setFilters: resolvedSetFilters,
    sorts: resolvedDraft.sorts,
    setSorts: resolvedSetSorts,
    includeDescendants: resolvedDraft.includeDescendants,
    setIncludeDescendants: resolvedSetIncludeDescendants,
    timelineField: resolvedDraft.timelineField,
    setTimelineField: resolvedSetTimelineField,
    timelineRowMode: resolvedDraft.timelineRowMode,
    setTimelineRowMode: resolvedSetTimelineRowMode,
    timelineGroupField: resolvedDraft.timelineGroupField,
    setTimelineGroupField: resolvedSetTimelineGroupField,
    showUnscheduled: resolvedDraft.showUnscheduled,
    setShowUnscheduled: resolvedSetShowUnscheduled,
    preset: resolvedDraft.preset || 'timeline',
    setPreset: resolvedSetPreset,
    showHierarchy: Boolean(resolvedDraft.showHierarchy),
    setShowHierarchy: resolvedSetShowHierarchy,
    showDependencies: Boolean(resolvedDraft.showDependencies),
    setShowDependencies: resolvedSetShowDependencies,
    allowScheduleEdit: Boolean(resolvedDraft.allowScheduleEdit),
    setAllowScheduleEdit: resolvedSetAllowScheduleEdit,
    applyTypeSelection,
    calendarField: resolvedDraft.calendarField || '',
    chartConfig: resolvedDraft.chartConfig,
    setChartConfig: resolvedSetChartConfig,
    scheduledType: resolvedDraft.scheduledType,
    setScheduledType: resolvedSetScheduledType,
    scheduledField: resolvedDraft.scheduledField,
    setScheduledField: resolvedSetScheduledField,
    slotIncrementMinutes: resolvedDraft.slotIncrementMinutes,
    setSlotIncrementMinutes: resolvedSetSlotIncrementMinutes,
    scheduledFilters: resolvedDraft.scheduledFilters,
    setScheduledFilters: resolvedSetScheduledFilters,
    scheduledProperties: resolvedDraft.scheduledProperties,
    setScheduledProperties: resolvedSetScheduledProperties,
  };
};

export default useViewModalState;
